import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sun, Moon, Coffee, Utensils, Briefcase,
  ChevronRight, ChevronLeft, Sparkles, Check,
  Zap, Calendar, Clock, X
} from 'lucide-react';
import weekGeneratorService from '../../services/weekGeneratorService';
import { saveScheduleSettings as saveScheduleFirestore, isAuthenticated } from '../../services/firestoreSync';

/**
 * SetupWizard - Assistant de configuration initiale
 * Guide l'utilisateur pour créer son premier planning
 */
export function SetupWizard({ isOpen, onClose, onComplete }) {
  const [step, setStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);

  // Configuration utilisateur
  const [config, setConfig] = useState({
    wakeTime: '07:00',
    sleepTime: '23:00',
    breakfastTime: '08:00',
    lunchTime: '12:30',
    dinnerTime: '19:30',
    workStartTime: '09:00',
    workEndTime: '18:00',
    template: 'balanced',
    includeWeekends: true,
    autoAddHygiene: true,
    autoAddBreaks: true
  });

  const templates = weekGeneratorService.getAvailableTemplates();

  const updateConfig = useCallback((key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleNext = useCallback(() => {
    if (step < 3) setStep(s => s + 1);
  }, [step]);

  const handleBack = useCallback(() => {
    if (step > 1) setStep(s => s - 1);
  }, [step]);

  const handleGenerate = useCallback(async () => {
    setIsGenerating(true);
    try {
      // Sauvegarder les paramètres
      const scheduleSettings = {
        wakeTime: config.wakeTime,
        sleepTime: config.sleepTime,
        breakfastTime: config.breakfastTime,
        lunchTime: config.lunchTime,
        dinnerTime: config.dinnerTime,
        workStartTime: config.workStartTime,
        workEndTime: config.workEndTime
      };
      localStorage.setItem('adhd_schedule_settings', JSON.stringify(scheduleSettings));
      localStorage.setItem('adhd_setup_completed', 'true');

      // Sync to Firestore
      if (isAuthenticated()) {
        saveScheduleFirestore(scheduleSettings).catch(() => {});
      }

      // Générer la semaine
      const today = new Date();
      const weekTasks = weekGeneratorService.generateWeek(today, {
        template: config.template,
        includeWeekends: config.includeWeekends,
        autoAddHygiene: config.autoAddHygiene,
        autoAddBreaks: config.autoAddBreaks,
        schedule: scheduleSettings
      });

      // Callback avec les tâches générées
      onComplete(weekTasks, scheduleSettings);
    } catch (error) {
      console.error('Erreur génération:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [config, onComplete]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-md bg-[#0e0e14] border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Configuration rapide</h2>
              <p className="text-sm text-zinc-500">Étape {step} sur 3</p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="flex gap-2">
            {[1, 2, 3].map(s => (
              <div
                key={s}
                className={`flex-1 h-1.5 rounded-full transition-colors ${
                  s <= step ? 'bg-gradient-to-r from-cyan-500 to-purple-500' : 'bg-zinc-800'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <Step1Schedule config={config} updateConfig={updateConfig} />
            )}
            {step === 2 && (
              <Step2Template config={config} updateConfig={updateConfig} templates={templates} />
            )}
            {step === 3 && (
              <Step3Options config={config} updateConfig={updateConfig} />
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex gap-3">
          {step > 1 && (
            <button
              onClick={handleBack}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-medium rounded-lg transition-all"
            >
              <ChevronLeft className="w-4 h-4" />
              Retour
            </button>
          )}

          {step < 3 ? (
            <button
              onClick={handleNext}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 text-white font-medium rounded-lg transition-all"
            >
              Continuer
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white font-medium rounded-lg transition-all disabled:opacity-50"
            >
              {isGenerating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Génération...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Générer ma semaine
                </>
              )}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}

/**
 * Step 1: Configuration des horaires
 */
function Step1Schedule({ config, updateConfig }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-zinc-200 mb-1">Tes horaires</h3>
        <p className="text-sm text-zinc-500">Personnalise ton planning selon ton rythme</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <TimeInput
          icon={Sun}
          label="Réveil"
          value={config.wakeTime}
          onChange={(v) => updateConfig('wakeTime', v)}
          color="amber"
        />
        <TimeInput
          icon={Moon}
          label="Coucher"
          value={config.sleepTime}
          onChange={(v) => updateConfig('sleepTime', v)}
          color="indigo"
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <TimeInput
          icon={Coffee}
          label="Petit-déj"
          value={config.breakfastTime}
          onChange={(v) => updateConfig('breakfastTime', v)}
          color="orange"
          compact
        />
        <TimeInput
          icon={Utensils}
          label="Déjeuner"
          value={config.lunchTime}
          onChange={(v) => updateConfig('lunchTime', v)}
          color="green"
          compact
        />
        <TimeInput
          icon={Utensils}
          label="Dîner"
          value={config.dinnerTime}
          onChange={(v) => updateConfig('dinnerTime', v)}
          color="purple"
          compact
        />
      </div>

      <div className="grid grid-cols-2 gap-3 pt-2">
        <TimeInput
          icon={Briefcase}
          label="Début travail"
          value={config.workStartTime}
          onChange={(v) => updateConfig('workStartTime', v)}
          color="blue"
        />
        <TimeInput
          icon={Briefcase}
          label="Fin travail"
          value={config.workEndTime}
          onChange={(v) => updateConfig('workEndTime', v)}
          color="blue"
        />
      </div>
    </motion.div>
  );
}

/**
 * Step 2: Choix du template
 */
function Step2Template({ config, updateConfig, templates }) {
  const templateIcons = {
    minimal: Clock,
    balanced: Calendar,
    structured: Briefcase,
    intense: Zap
  };

  const templateColors = {
    minimal: 'from-zinc-500/20 to-zinc-600/20 border-zinc-600/50',
    balanced: 'from-emerald-500/20 to-cyan-500/20 border-emerald-500/50',
    structured: 'from-blue-500/20 to-indigo-500/20 border-blue-500/50',
    intense: 'from-orange-500/20 to-red-500/20 border-orange-500/50'
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-zinc-200 mb-1">Choisis ton style</h3>
        <p className="text-sm text-zinc-500">Sélectionne le template qui te correspond</p>
      </div>

      <div className="space-y-3">
        {templates.map(template => {
          const Icon = templateIcons[template.id] || Calendar;
          const isSelected = config.template === template.id;

          return (
            <button
              key={template.id}
              onClick={() => updateConfig('template', template.id)}
              className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                isSelected
                  ? `bg-gradient-to-r ${templateColors[template.id]} border-opacity-100`
                  : 'bg-zinc-800/30 border-zinc-800 hover:border-zinc-700'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  isSelected ? 'bg-white/10' : 'bg-zinc-700/50'
                }`}>
                  <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-zinc-400'}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold ${isSelected ? 'text-white' : 'text-zinc-300'}`}>
                      {template.name}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      isSelected ? 'bg-white/20 text-white' : 'bg-zinc-700 text-zinc-400'
                    }`}>
                      ~{template.tasksPerDay} tâches/jour
                    </span>
                    {isSelected && (
                      <Check className="w-4 h-4 text-emerald-400 ml-auto" />
                    )}
                  </div>
                  <p className={`text-sm mt-1 ${isSelected ? 'text-zinc-300' : 'text-zinc-500'}`}>
                    {template.description}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}

/**
 * Step 3: Options supplémentaires
 */
function Step3Options({ config, updateConfig }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-zinc-200 mb-1">Derniers réglages</h3>
        <p className="text-sm text-zinc-500">Personnalise encore plus ton planning</p>
      </div>

      <div className="space-y-3">
        <OptionToggle
          label="Inclure les weekends"
          description="Génère aussi samedi et dimanche"
          checked={config.includeWeekends}
          onChange={(v) => updateConfig('includeWeekends', v)}
        />
        <OptionToggle
          label="Tâches d'hygiène"
          description="Douche, brossage de dents, etc."
          checked={config.autoAddHygiene}
          onChange={(v) => updateConfig('autoAddHygiene', v)}
        />
        <OptionToggle
          label="Pauses automatiques"
          description="Ajoute des pauses café et détente"
          checked={config.autoAddBreaks}
          onChange={(v) => updateConfig('autoAddBreaks', v)}
        />
      </div>

      {/* Preview */}
      <div className="mt-6 p-4 bg-zinc-800/30 border border-zinc-700/50 rounded-xl">
        <p className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-3">Aperçu</p>
        <div className="flex items-center justify-between text-sm">
          <span className="text-zinc-400">Template</span>
          <span className="text-zinc-200 font-medium capitalize">{config.template}</span>
        </div>
        <div className="flex items-center justify-between text-sm mt-2">
          <span className="text-zinc-400">Jours générés</span>
          <span className="text-zinc-200 font-medium">
            {config.includeWeekends ? '7 jours' : '5 jours (Lun-Ven)'}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm mt-2">
          <span className="text-zinc-400">Horaires</span>
          <span className="text-zinc-200 font-medium">{config.wakeTime} - {config.sleepTime}</span>
        </div>
      </div>
    </motion.div>
  );
}

/**
 * TimeInput - Input pour les horaires
 */
function TimeInput({ icon: Icon, label, value, onChange, color = 'zinc', compact = false }) {
  const colorClasses = {
    amber: 'bg-amber-500/10 text-amber-400',
    indigo: 'bg-indigo-500/10 text-indigo-400',
    orange: 'bg-orange-500/10 text-orange-400',
    green: 'bg-green-500/10 text-green-400',
    purple: 'bg-purple-500/10 text-purple-400',
    blue: 'bg-blue-500/10 text-blue-400',
    zinc: 'bg-zinc-500/10 text-zinc-400'
  };

  return (
    <div className={`bg-zinc-800/50 border border-zinc-700/50 rounded-lg ${compact ? 'p-2' : 'p-3'}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-6 h-6 rounded flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <span className={`font-medium text-zinc-300 ${compact ? 'text-xs' : 'text-sm'}`}>{label}</span>
      </div>
      <input
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full bg-zinc-900/50 border border-zinc-700/50 rounded px-2 py-1.5 text-white font-medium outline-none focus:border-cyan-500/50 ${
          compact ? 'text-sm' : 'text-base'
        }`}
      />
    </div>
  );
}

/**
 * OptionToggle - Toggle pour les options
 */
function OptionToggle({ label, description, checked, onChange }) {
  return (
    <label className="flex items-center justify-between p-4 bg-zinc-800/30 border border-zinc-700/50 rounded-xl cursor-pointer hover:bg-zinc-800/50 transition-all">
      <div>
        <span className="text-sm font-medium text-zinc-200 block">{label}</span>
        <span className="text-xs text-zinc-500">{description}</span>
      </div>
      <div className="relative">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only peer"
        />
        <div className="w-11 h-6 bg-zinc-700 rounded-full peer peer-checked:bg-gradient-to-r peer-checked:from-cyan-500 peer-checked:to-purple-500 transition-all" />
        <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow peer-checked:translate-x-5 transition-transform" />
      </div>
    </label>
  );
}

export default SetupWizard;
